import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Is Missionary Support Tax-Deductible in the UK? Gift Aid Explained (2026)';
const DESCRIPTION =
    "A plain-English guide for UK donors: when giving to a missionary qualifies for Gift Aid, when it doesn't, and why the answer comes down to who actually receives the gift.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover18.jpg',
            datePublished: '2026-08-14',
            dateModified: '2026-08-14',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/is-missionary-support-tax-deductible-uk',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Can a UK donor claim Gift Aid on money given directly to a missionary?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "No. Gift Aid only applies to donations made to a charity registered with the Charity Commission (or HMRC-recognised equivalent). A personal gift to an individual missionary, even for ministry work, does not qualify — the recipient has to be the charity itself, not a person.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How does Gift Aid actually work?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "A UK taxpayer donates to a registered charity and signs a Gift Aid declaration. The charity then reclaims basic-rate tax (25p for every £1 given) from HMRC on top of the donation. Higher-rate taxpayers can additionally claim the difference between their rate and basic rate on their own Self Assessment return.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How do UK missionaries offer Gift Aid-eligible giving?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Through a missionary society, sending agency, or fiscal host that is itself a registered UK charity. The donor gives to the charity, which retains discretion over the funds even when it routes most of them to a named missionary's field account.",
                    },
                },
            ],
        },
    ],
};

export default function IsMissionarySupportTaxDeductibleUK() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/is-missionary-support-tax-deductible-uk' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-tax-deductible-uk-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover18.jpg" alt="A UK supporter reviewing a Gift Aid declaration form" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>"Can I Gift Aid this?" is usually the second question a UK supporter asks, right after "how much do you need?" The honest answer has nothing to do with how faithful the missionary is — it's entirely about whether a registered charity sits between the donor and the money.</p>

                    <h2>The short answer</h2>
                    <p>Gift Aid only applies to a donation made to a charity registered with the Charity Commission for England and Wales (or the equivalent Scottish/Northern Irish regulator). A gift handed, transferred, or sent by card straight to an individual missionary — no matter how legitimate the ministry — is a personal gift, not a charitable donation, and HMRC will not let a charity reclaim tax on it.</p>

                    <h2>Why a direct gift to a missionary doesn't qualify</h2>
                    <p>HMRC's rule is about who receives the gift, full stop. If a supporter sends money directly to a missionary's personal bank account to cover rent, flights, or living costs, that's the same as giving money to a friend or relative — generous, but not Gift-Aidable. This is true of most direct, platform-based personal support pages, including GoFundMe-style campaigns and recurring-support platforms like ours that pay a creator directly rather than routing through a registered charity.</p>

                    <h2>How Gift Aid-eligible missionary giving actually works</h2>
                    <p>When a missionary offers Gift Aid, there's a UK-registered charity in the chain — a missionary society (e.g. a denominational mission board), an independent sending agency, or a dedicated fiscal-hosting charity. The donor's gift goes to the charity, the donor signs a Gift Aid declaration, and the charity reclaims 25p for every £1 given from HMRC — even though in practice most of that money is then directed to a named missionary's field account. The charity has to keep genuine discretion over the funds; it can't act as a pure pass-through for a donation earmarked to one individual, or it stops qualifying.</p>
                    <p>Higher and additional-rate taxpayers get a second benefit: they can claim the difference between their marginal rate and the 20% basic rate back on their own Self Assessment return, on top of what the charity already reclaimed.</p>

                    <h2>Why this matters less than people assume</h2>
                    <p>Gift Aid is valuable, but it doesn't change whether the donor themself is out of pocket for anything beyond the gift — it's the charity, not the donor, that receives the extra 25p. For a basic-rate donor giving £30/month, that's real money for the ministry (£7.50/month extra) but zero difference to the donor's own tax bill. It mainly changes outcomes for higher-rate donors making sizeable gifts, and for the receiving organisation's total income.</p>

                    <h2>What this means if you're choosing how to give — or built to receive</h2>
                    <p>If your UK supporter base is mostly modest, regular monthly givers, the simplicity of direct support usually outweighs the Gift Aid uplift most of them won't individually feel. If a meaningful share of your list gives at scale and cares about maximising their impact, routing through a registered mission agency or fiscal host is worth the extra structure, approval process, and administrative fee that comes with it.</p>
                    <p>We're upfront that Devotion Fund is a direct-support platform: gifts aren't Gift Aid-eligible today, because we're not a registered charity and don't route funds through one. If Gift Aid is the deciding factor for your support base, a UK mission agency or fiscal sponsor is the more honest fit.</p>

                    <h2>Frequently asked questions</h2>
                    <p><strong>Can a UK donor claim Gift Aid on money given directly to a missionary?</strong><br />
                    No — Gift Aid requires the recipient to be a registered charity, not an individual.</p>
                    <p><strong>How does Gift Aid actually work?</strong><br />
                    The charity reclaims 25p per £1 from HMRC after the donor signs a Gift Aid declaration; higher-rate taxpayers can claim further relief themselves.</p>
                    <p><strong>How do UK missionaries offer Gift Aid-eligible giving?</strong><br />
                    Through a registered mission agency or fiscal host that retains discretion over the funds, rather than passing a donor's gift straight through to a named individual.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/is-missionary-support-tax-deductible">Is Missionary Support Tax-Deductible? (US guide)</a></li>
                        <li><a href="/resources/is-missionary-support-tax-deductible-canada">Is Missionary Support Tax-Deductible in Canada?</a></li>
                        <li><a href="/resources/how-to-start-monthly-giving-page-without-501c3">How to Start a Monthly Giving Page Without a 501(c)(3)</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We run a direct, recurring-first support platform — which means being straight with UK supporters about what it doesn't do, including Gift Aid, is part of the job.</p>
                    <p><small><em>Last updated: 2026-08-14. This article is general information, not tax advice — talk to a qualified UK tax adviser or accountant about your specific situation.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/is-missionary-support-tax-deductible-uk" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
